import { useState } from "react";
import { View, useWindowDimensions } from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Heart, Compass, HeartHandshake, Users } from "lucide-react-native";
import { useOnboarding } from "../OnboardingContext";
import OnboardingLayout, {
  ScreenTitle,
  ScreenSubtitle,
} from "../components/OnboardingLayout";
import GlassChoiceButton from "../components/GlassChoiceButton";
import { AUTO_ADVANCE_DELAY } from "../theme";
import { useTranslations } from "@src/packages/i18n";
import { getOnboardingViewport } from "../onboardingViewport";
import type { OnboardingStackParamList } from "../types";

type Props = NativeStackScreenProps<
  OnboardingStackParamList,
  "WorkingWithSurrogate"
>;

type Choice = "carry" | "partner" | "considering" | "surrogate";

const OPTIONS: {
  value: Choice;
  labelKey: string;
  sublabelKey: string;
  Icon: typeof Heart;
}[] = [
  {
    value: "carry",
    labelKey: "onboarding.working_with_surrogate.carry_label",
    sublabelKey: "onboarding.working_with_surrogate.carry_sublabel",
    Icon: Heart,
  },
  {
    value: "partner",
    labelKey: "onboarding.working_with_surrogate.partner_label",
    sublabelKey: "onboarding.working_with_surrogate.partner_sublabel",
    Icon: Users,
  },
  {
    value: "considering",
    labelKey: "onboarding.working_with_surrogate.considering_label",
    sublabelKey: "onboarding.working_with_surrogate.considering_sublabel",
    Icon: Compass,
  },
  {
    value: "surrogate",
    labelKey: "onboarding.working_with_surrogate.surrogate_label",
    sublabelKey: "onboarding.working_with_surrogate.surrogate_sublabel",
    Icon: HeartHandshake,
  },
];

export default function WorkingWithSurrogateScreen({ navigation }: Props) {
  const { width, height } = useWindowDimensions();
  const { condensed } = getOnboardingViewport(width, height);
  const compactChoices = condensed || width < 430;
  const t = useTranslations();
  const { updateField } = useOnboarding();
  const [selected, setSelected] = useState<Choice | null>(null);

  const handleSelect = (choice: Choice) => {
    setSelected(choice);
    if (choice === "surrogate") {
      updateField("treatmentIntent", "surrogacy");
      updateField("journeyRole", "intended-parent");
    }
    setTimeout(() => navigation.navigate("Sex"), AUTO_ADVANCE_DELAY);
  };

  return (
    <OnboardingLayout>
      <ScreenTitle>{t("onboarding.working_with_surrogate.title")}</ScreenTitle>
      <ScreenSubtitle>
        {t("onboarding.working_with_surrogate.subtitle")}
      </ScreenSubtitle>
      <View style={{ gap: compactChoices ? 10 : 14 }}>
        {OPTIONS.map((option) => (
          <GlassChoiceButton
            key={option.value}
            Icon={option.Icon}
            label={t(option.labelKey)}
            sublabel={t(option.sublabelKey)}
            selected={selected === option.value}
            compact={compactChoices}
            onPress={() => handleSelect(option.value)}
          />
        ))}
      </View>
    </OnboardingLayout>
  );
}
